import { Box, Divider, Flex, Modal, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';
import Button from 'src/components/design-system/buttons/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/suitability-report/hooks/useDocumentVersions';
import { FinaliseVersionCell } from './versioning/FinaliseVersionCell';

type Props = {
  opened: boolean;
  onClose: () => void;
  primaryVersion: DocumentVersion;
  secondaryVersions: DocumentVersion[];
  onConfirm: () => Promise<void>;
};

export function FinaliseDocumentModal({
  opened,
  onClose,
  primaryVersion,
  secondaryVersions,
  onConfirm,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="xxl"
      variant="multi"
    >
      <Flex style={{ borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
        <Box
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-gray-10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1,
          }}
        >
          <IconX size={12} color="white" />
        </Box>

        <Flex direction="column" justify="space-between" bg="white" p="xxxl" style={{ flex: 1 }}>
          <Stack gap="xxs">
            <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="black">
              Finalising document
            </Text>
            <Text size="textMd" fw={FONT_WEIGHTS.medium} c="black">
              Only the primary and most recent versions will be kept, older versions will be removed
              permanently.
            </Text>
          </Stack>

          <Stack gap="xs" mt="xxxl">
            <Button size="xl" onClick={handleConfirm} loading={isLoading}>
              Finalise & remove versions
            </Button>
            <Button size="xl" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Flex>

        <Flex direction="column" bg="gray.0" p="xxxl" style={{ flex: 1 }}>
          <Stack gap={0}>
            <Text size="xs" fw={FONT_WEIGHTS.medium} c="success.7">
              Will be saved & finalised
            </Text>
            <Box mt="xs">
              <FinaliseVersionCell version={primaryVersion} variant="saved" />
            </Box>

            {secondaryVersions.length > 0 && (
              <>
                <Divider color="gray.1" my="md" />

                <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6">
                  The following versions will remain
                </Text>
                <Stack gap="xs" mt="xs">
                  {secondaryVersions.map(version => (
                    <FinaliseVersionCell key={version.id} version={version} variant="retained" />
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Flex>
      </Flex>
    </Modal>
  );
}
